import { VarReason, VarStatus } from '@uefacom-newdev/liveblog-integration-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { hydrateWithTranslations } from '../services/TranslatorService';

import { FORCE_REFRESH } from './browser';
import { dateToString } from './dateTime';

import type { Match, Team, HomeAway, RelatedMatch, PlayerEvent, LiveMatch } from '@shared-types/match.types';
import {
  RoundPhase,
  MatchStatus,
  TeamType,
  LineupStatus,
  MatchType,
  MatchWinReason,
  MatchWinner,
  TeamCategory,
  EventType,
  MatchPhase,
} from '@shared-types/match.types';

dayjs.extend(utc);

export interface PollingTimes {
  LIVE: number;
  NEAR: number;
  DEFAULT: number;
}

export const PollingTimes: PollingTimes = {
  LIVE: 10 * 1000, // 10 seconds (status LIVE or <3h before/after match)
  NEAR: 60 * 1000, // 1 minute (<8h before match)
  DEFAULT: -1, // polling disabled
};

export const getRefreshRate = (match: Match, pollingTimes: PollingTimes = PollingTimes) => {
  if (!match) return pollingTimes.DEFAULT;

  if (FORCE_REFRESH || match.status?.toUpperCase() === MatchStatus.LIVE) {
    return pollingTimes.LIVE;
  }

  const md = dayjs(match.kickOffTime?.dateTime ?? match.kickOffTime?.date ?? 'invalid');
  if (!md.isValid()) return pollingTimes.DEFAULT;

  const now = dayjs();
  const hoursBefore = md.diff(now, 'hour');

  const matchEnd = dayjs(match.fullTimeAt ?? md.add(2, 'hours'));
  const hoursFromEnd = now.diff(matchEnd, 'hour');

  if (hoursBefore < 3 && hoursFromEnd < 3) return pollingTimes.LIVE;

  if (hoursBefore > 0 && hoursBefore < 8) return pollingTimes.NEAR;

  return pollingTimes.DEFAULT;
};

export const isFullMatch = (match: LiveMatch) =>
  match.loaded || (match.homeTeam && match.awayTeam && match.competition);

export const isPlaceholderMatch = (match: Match) =>
  (!match.homeTeam || match.homeTeam?.typeTeam === TeamType.PLACEHOLDER) &&
  (!match.awayTeam || match.awayTeam?.typeTeam === TeamType.PLACEHOLDER);

export const isFake = (team: Team) => !team?.id || team.typeTeam?.toUpperCase() === TeamType.PLACEHOLDER;

export const isNational = (team: Team) => team?.typeTeam?.toUpperCase() === TeamType.NATIONAL;

export const isNationsLeague = (cup: string, phase: RoundPhase) =>
  window.competitionId === '2014' ||
  `${cup}` === '2014' ||
  (window.competitionId === '18' && phase === RoundPhase.PREQUALIFYING) ||
  (`${cup}` === '18' && phase === RoundPhase.PREQUALIFYING);

export const isRegions = (cup: string) => `${cup}` === '22';

export const isFriendly = (match: Match) => match?.competition?.id === '19';

export const isFutsal = (match: Match) => match?.competition?.sportsType?.toUpperCase() === 'FUTSAL';

export const matchWinner = (match: Match | RelatedMatch, useAggregate = true) =>
  hydrateWithTranslations(useAggregate ? (match.winner?.aggregate ?? match.winner?.match) : match.winner?.match);

export const isSecondLegMatch = (match: Match) => match.type === MatchType.SECOND_LEG || `${match.leg?.number}` === '2';

export const cleanupDomesticMatch = (match: Match) => {
  if (
    match?.competition?.region === 'DOMESTIC' &&
    !!match.kickOffTime?.dateTime &&
    dayjs(match.kickOffTime.dateTime).utc().hour() === 0 &&
    dayjs(match.kickOffTime.dateTime).utc().minute() === 0
  ) {
    return {
      ...match,
      kickOffTime: {
        ...match.kickOffTime,
        dateTime: null,
      },
    };
  }
  return match;
};

export const matchWinnerHa = (match: Match, useAggregate = isSecondLegMatch(match)) => {
  const winner = matchWinner(match, useAggregate);
  if (!winner) return '';
  if (winner.reason === MatchWinReason.DRAW || !winner.team) return MatchWinner.DRAW;
  return winner.team.id === match.homeTeam?.id ? MatchWinner.HOME : MatchWinner.AWAY;
};

export const matchResult = (match: Match, teamId: string) => {
  const winner = matchWinner(match, false);
  if (!winner || winner.reason === MatchWinReason.DRAW) return 'draw';
  return winner.team?.id === teamId ? 'won' : 'lost';
};

export const matchLink = (href?: string) => (href ?? '').replace('/worldcup/', '/european-qualifiers/');

export const matchDate = (match: Match, format = 'YYYY-MM-DD') => {
  const date = match.kickOffTime?.dateTime ?? match.kickOffTime?.date;

  if (!date) return '';

  return dateToString(date, format);
};

const casesStatsStatus = [
  MatchStatus.LIVE,
  MatchStatus.ABANDONED,
  MatchStatus.SUSPENDED,
  MatchStatus.FINISHED,
] as MatchStatus[];

const lineupAvailableStates = [LineupStatus.AVAILABLE, LineupStatus.TACTICAL_AVAILABLE] as LineupStatus[];

export const matchLineupsAvailable = (match: Match) => lineupAvailableStates.includes(match?.lineupStatus);

export const matchTacticalLineupsAvailable = (match: Match) =>
  !isFutsal(match) && match.lineupStatus === LineupStatus.TACTICAL_AVAILABLE;

export const matchStatisticsAvailable = (match: Match) => casesStatsStatus.includes(match?.status);

export const matchScore = (match: Match | RelatedMatch, ha: HomeAway) => match.score?.total?.[ha];

export const getSeasonLabelByMatch = (match: Match, shortened = true) => {
  if (!match?.competition || !match?.seasonYear) return;

  const {
    seasonYear,
    competition: { id: competitionId, teamCategory },
  } = match;

  return getSeasonLabel(seasonYear, competitionId, teamCategory, shortened);
};

export const getSeasonLabel = (
  seasonYear: string,
  competitionId: string,
  teamCategory: TeamCategory,
  shortened = true,
) => {
  const year = parseInt(seasonYear);
  if (Number.isNaN(year) || year <= 0) {
    return seasonYear;
  }

  if (competitionId === '18' && year === 2021) {
    return '2022';
  }

  if (teamCategory === TeamCategory.NATIONAL) {
    return seasonYear;
  } else {
    if (year < 2008 && ['1', '14', '28'].includes(competitionId)) {
      return `${year}/${shortened ? (year + 1).toString().substring(seasonYear.length - 2) : year + 1}`;
    }

    return `${year - 1}/${shortened ? seasonYear.substring(seasonYear.length - 2) : seasonYear}`;
  }
};

export const timelineEventTypes = [
  EventType.GOAL,
  EventType.RED_CARD,
  EventType.YELLOW_CARD,
  EventType.RED_YELLOW_CARD,
  EventType.SUBSTITUTION,
  EventType.START_PHASE,
  EventType.END_PHASE,
  EventType.PENALTY,
  EventType.INJURY_TIME,
] as const;
export const includeTimelineEvent = (includeVarEvents?: boolean) => (e: PlayerEvent) =>
  (timelineEventTypes.includes(e.type) || (includeVarEvents && e.type === EventType.VAR)) &&
  (e.type !== EventType.START_PHASE || e.phase === MatchPhase.FIRST_HALF) &&
  (e.type !== EventType.VAR ||
    (e.varInfo?.reason !== VarReason.NONE && [VarStatus.CONFIRMED, VarStatus.CHANGED].includes(e.varInfo?.status)));
